// Copyright (c) 2026 PGNS LLC

import type { HistoryDay } from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatAvailability, formatDay, STATUS_FILL, STATUS_LABEL } from '@/lib/status';

/**
 * The 90-day uptime strip.
 *
 * One element per day in the window, including days the prober never measured
 * — those render as `unknown` gaps rather than being dropped, so the strip's
 * width is the window and not a claim about coverage.
 */
export function UptimeBar({ days }: { days: HistoryDay[] }) {
  if (days.length === 0) {
    return <p className="text-sm text-muted-foreground">No history recorded yet.</p>;
  }
  const first = days[0];
  const last = days.at(-1);
  return (
    <div>
      <div
        className="flex items-end gap-[2px] h-9"
        role="img"
        aria-label={`Daily availability for the last ${days.length} days`}
      >
        {days.map((day) => (
          <span
            key={day.day}
            title={`${formatDay(day.day)} — ${STATUS_LABEL[day.status]} (${formatAvailability(day.availability)})`}
            className={cn(
              'flex-1 min-w-[2px] h-full rounded-[1px] transition-opacity hover:opacity-70',
              STATUS_FILL[day.status],
            )}
          />
        ))}
      </div>
      <div className="mt-2 flex justify-between text-xs text-muted-foreground">
        <span>{first ? formatDay(first.day) : ''}</span>
        <span>{days.length} days</span>
        <span>{last ? formatDay(last.day) : ''}</span>
      </div>
    </div>
  );
}
